export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  avatar?: string;
  role: UserRole;
  organizationId: string;
  status: 'active' | 'inactive' | 'pending';
  lastLogin?: Date;
  createdAt: Date;
  updatedAt: Date;
}

export type UserRole = 'admin' | 'developer' | 'viewer' | 'billing_admin';

export interface Organization {
  id: string;
  name: string;
  slug: string;
  logo?: string;
  plan: SubscriptionPlan;
  industry?: string;
  productLine?: 'trust-services';
  operatingModel?: 'relying-party';
  rolePack?: string;
  provisionedRoles?: string[];
  size?: 'startup' | 'small' | 'medium' | 'enterprise';
  settings: OrganizationSettings;
  createdAt: Date;
  updatedAt: Date;
}

export interface OrganizationSettings {
  allowSSOLogin: boolean;
  requireMFA: boolean;
  apiKeyExpiration: number; // days
  maxTeamMembers: number;
  allowedDomains: string[];
  webhookEndpoints: string[];
}

export type SubscriptionPlan = 'free' | 'starter' | 'professional' | 'enterprise';

export interface Subscription {
  id: string;
  organizationId: string;
  plan: SubscriptionPlan;
  status: 'active' | 'canceled' | 'past_due' | 'trialing';
  currentPeriodStart: Date;
  currentPeriodEnd: Date;
  cancelAtPeriodEnd: boolean;
  trialEndsAt?: Date;
  usage: {
    apiCalls: number;
    monthlyLimit: number;
  };
  decisionControl?: {
    mode: 'AI_ASSISTED' | 'HUMAN_IN_LOOP' | 'AI_AUTONOMOUS';
    regulatedBankPolicy: boolean;
    auditLoggingLocked: true;
    allowAutoApproveLowRiskKyc: boolean;
    allowAutoRejectFraud: boolean;
    auditRetentionDays: number;
    policyVersion: string;
  };
}

export interface APIKey {
  id: string;
  name: string;
  key: string;
  organizationId: string;
  userId: string;
  permissions: APIPermission[];
  environment: 'production' | 'sandbox';
  lastUsed?: Date;
  usageCount: number;
  rateLimit: number;
  expiresAt?: Date;
  isActive: boolean;
  createdAt: Date;
}

export interface ApiApplication {
  id: string;
  name: string;
  contactEmail: string;
  planId: string;
  redirectUrls: string[];
  redirectUrlReasons?: string;
  publicLogoUrl?: string;
  faydaReadiness?: FaydaReadiness;
  clientId: string;
  /** Returned by the backend on every GET today; rotate via the rotate-keys endpoint. */
  clientSecret: string;
  createdAt: Date;
  lastKeyRotation?: Date;
  isActive: boolean;
}

export interface FaydaReadiness {
  rootCaCertificate: boolean;
  intermediateCertificate: boolean;
  partnerLeafCertificate: boolean;
  insaSecurityClearance: boolean;
}

export interface ApiPlan {
  id: string;
  productId: string;
  name: string;
  description?: string;
  monthlyCost: number;
  rateLimitPerMinute: number;
  isDefault: boolean;
}

export type APIPermission =
  | 'identity:verify' 
  | 'identity:read' 
  | 'aml:screen' 
  | 'aml:read'
  | 'fraud:analyze' 
  | 'fraud:read'
  | 'banking:connect' 
  | 'banking:read'
  | 'webhooks:manage';

export interface AuthState {
  user: User | null;
  organization: Organization | null;
  subscription: Subscription | null;
  loading: boolean;
  isAuthenticated: boolean;
}

// Enhanced Auth Context
export interface AuthContextType extends AuthState {
  signIn: (email: string, password: string) => Promise<void>;
  signInWithProvider: (provider: 'github') => Promise<void>;
  signOut: () => Promise<void>;
  signUp: (userData: SignUpData) => Promise<void>;
  updateProfile: (data: Partial<User>) => Promise<void>;
  switchOrganization: (orgId: string) => Promise<void>;
  hasPermission: (permission: string) => boolean;
  hasRole: (role: UserRole | UserRole[]) => boolean;
}

export interface SignUpData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  organizationName: string;
  industry?: string;
  companySize?: string;
}

export interface LoginCredentials {
  email: string;
  password: string;
  rememberMe?: boolean;
}

// Role-based permissions
export const ROLE_PERMISSIONS: Record<UserRole, string[]> = {
  admin: [
    'identity:*', 'aml:*', 'fraud:*', 'banking:*', 'webhooks:*',
    'api-keys:manage', 'decision-control:manage', 'team:manage', 'billing:manage', 'settings:manage', 'analytics:view'
  ],
  developer: [
    'identity:verify', 'identity:read', 'aml:screen', 'aml:read',
    'fraud:analyze', 'fraud:read', 'banking:connect', 'banking:read',
    'webhooks:manage', 'api-keys:manage', 'analytics:view'
  ],
  viewer: [
    'identity:read', 'aml:read', 'fraud:read', 'banking:read', 'analytics:view'
  ],
  billing_admin: [
    'billing:manage', 'team:view', 'analytics:view'
  ]
};

export const PLAN_LIMITS: Record<SubscriptionPlan, {
  monthlyApiCalls: number;
  teamMembers: number;
  apiKeys: number;
  webhooks: number;
  features: string[];
}> = {
  free: {
    monthlyApiCalls: 1000,
    teamMembers: 2,
    apiKeys: 2,
    webhooks: 1,
    features: ['Basic API Access', 'Email Support']
  },
  starter: {
    monthlyApiCalls: 10000,
    teamMembers: 5,
    apiKeys: 10,
    webhooks: 5,
    features: ['Advanced Analytics', 'Priority Support', 'Webhooks']
  },
  professional: {
    monthlyApiCalls: 100000,
    teamMembers: 20,
    apiKeys: 50,
    webhooks: 20,
    features: ['Custom Integrations', 'SLA Guarantee', 'Advanced Security']
  },
  enterprise: {
    monthlyApiCalls: -1, // unlimited
    teamMembers: -1, // unlimited
    apiKeys: -1, // unlimited
    webhooks: -1, // unlimited
    features: ['SSO', 'Dedicated Support', 'Custom Contracts', 'Audit Logs']
  }
};
